import { formatTime, type ZmanId } from "@kedaat/engine";
import type { ZmanRow } from "@/lib/zmanim";

/** Zmanim drawn as dots on the timeline; the labeled ones also get their time underneath. */
const DOTS: ZmanId[] = [
  "misheyakir",
  "sof_zman_shma",
  "sof_zman_tfila",
  "mincha_gedola",
  "mincha_ketana",
  "plag_hamincha",
];
const LABELED: ZmanId[] = ["sunrise", "chatzot", "sunset"];

/**
 * The day from dawn to nightfall as a sky-colored bar, flowing right to left, with the main
 * zmanim and the current moment. Decorative: the same information is in the list below.
 */
export function DayTimeline({ rows, now }: { rows: ZmanRow[]; now: number | null }) {
  const at = (id: ZmanId) => rows.find((r) => r.zman.id === id);
  const start = at("alos")?.ms;
  const end = at("tzeit")?.ms;
  const sunrise = at("sunrise")?.ms;
  const sunset = at("sunset")?.ms;
  if (start == null || end == null || sunrise == null || sunset == null || end <= start) {
    return null;
  }

  const position = (ms: number) => Math.min(100, Math.max(0, ((ms - start) / (end - start)) * 100));
  const rise = position(sunrise);
  const set = position(sunset);
  const sky = `linear-gradient(to left, var(--sky-night) 0%, var(--sky-dawn) ${rise}%, var(--sky-day) ${Math.min(rise + 8, 50)}%, var(--sky-day) ${Math.max(set - 8, 50)}%, var(--sky-dusk) ${set}%, var(--sky-night) 100%)`;
  const nowPosition = now !== null && now >= start && now <= end ? position(now) : null;
  const isDaylight = now !== null && now >= sunrise && now <= sunset;
  const time = (row: ZmanRow | undefined) =>
    row?.ms == null ? "" : formatTime(row.ms, row.opinion.rounding);

  return (
    <figure
      role="img"
      aria-label={`ציר היום: מעלות השחר ${time(at("alos"))} עד צאת הכוכבים ${time(at("tzeit"))}`}
      className="rounded-2xl border border-line bg-card px-4 pb-3 pt-4 shadow-sm"
    >
      <div className="flex justify-between text-xs text-muted" aria-hidden="true">
        <span>עלות השחר {time(at("alos"))}</span>
        <span>צאת הכוכבים {time(at("tzeit"))}</span>
      </div>
      <div
        className="relative mt-2 h-4 rounded-full ring-1 ring-inset ring-black/10"
        style={{ background: sky }}
        aria-hidden="true"
      >
        {DOTS.map((id) => {
          const row = at(id);
          if (row?.ms == null) return null;
          return (
            <span
              key={id}
              title={`${row.zman.label.he} ${time(row)}`}
              className="absolute top-1/2 size-1.5 rounded-full bg-white/85 ring-1 ring-black/15"
              style={{
                insetInlineStart: `${position(row.ms)}%`,
                transform: "translate(50%, -50%)",
              }}
            />
          );
        })}
        {nowPosition !== null && (
          /* The sun by day, the moon by night, standing where we are now. */
          <span
            className="absolute top-1/2 size-5 rounded-full ring-2 ring-card"
            style={{
              insetInlineStart: `${nowPosition}%`,
              transform: "translate(50%, -50%)",
              background: isDaylight
                ? "radial-gradient(circle at 35% 30%, #fff6d8, #f6c445)"
                : "radial-gradient(circle at 35% 30%, #ffffff, #c9d3e8)",
              boxShadow: isDaylight
                ? "0 0 0 4px rgba(246,196,69,0.28)"
                : "0 0 0 4px rgba(201,211,232,0.28)",
            }}
          />
        )}
      </div>
      <div className="relative mt-2.5 h-9" aria-hidden="true">
        {LABELED.map((id) => {
          const row = at(id);
          if (row?.ms == null) return null;
          return (
            <span
              key={id}
              className="absolute top-0 flex flex-col items-center whitespace-nowrap text-center leading-tight"
              style={{ insetInlineStart: `${position(row.ms)}%`, transform: "translateX(50%)" }}
            >
              <span className="text-[0.7rem] text-muted">{row.zman.short.he}</span>
              <span className="text-sm font-semibold tabular-nums">{time(row)}</span>
            </span>
          );
        })}
      </div>
    </figure>
  );
}
